import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, MailOpen, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Field } from "./useAdminApi";

const PageEditor = ({ page, call }) => {
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content);
  const save = async () => { try { await call("put", `/admin/pages/${page.slug}`, { title, content }); toast.success("Page enregistrée"); } catch { /* toast shown */ } };
  return (
    <div className="rounded-[20px] bg-white p-4 shadow-sm" data-testid={`admin-page-${page.slug}`}>
      <Field label="Titre"><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`admin-page-title-${page.slug}`} /></Field>
      <div className="mt-3"><Field label="Texte (## pour un titre, - pour une liste, ligne vide entre les paragraphes)">
        <Textarea rows={12} value={content} onChange={(e) => setContent(e.target.value)} data-testid={`admin-page-content-${page.slug}`} />
      </Field></div>
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={save} className="rounded-full bg-[#38B6FF] hover:bg-[#1fa3ee]" data-testid={`admin-page-save-${page.slug}`}>Enregistrer</Button>
        <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="text-sm underline">Voir la page</a>
      </div>
    </div>
  );
};

export const PagesTab = ({ call }) => {
  const [pages, setPages] = useState([]);
  useEffect(() => { call("get", "/admin/pages").then(setPages).catch(() => {}); }, [call]);
  return <div className="space-y-5">{pages.map((p) => <PageEditor key={p.slug} page={p} call={call} />)}</div>;
};

export const MessagesTab = ({ call }) => {
  const [msgs, setMsgs] = useState([]);
  const load = () => call("get", "/admin/messages").then(setMsgs).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const read = async (id) => { try { await call("patch", `/admin/messages/${id}/read`); } catch { return; } load(); };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try { await call("delete", `/admin/messages/${id}`); } catch { return; } load();
  };
  return (
    <ul className="space-y-3" data-testid="admin-messages-list">
      {msgs.length === 0 && <li className="text-sm opacity-70">Aucun message pour l'instant.</li>}
      {msgs.map((m) => (
        <li key={m.id} className={`rounded-[20px] p-4 shadow-sm ${m.read ? "bg-white/70" : "bg-white border-2 border-[#38B6FF]"}`} data-testid={`admin-message-${m.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            {m.read ? <MailOpen className="h-4 w-4 opacity-60" /> : <Mail className="h-4 w-4 text-[#38B6FF]" />}
            <p className="font-bold">{m.name}</p>
            <a href={`mailto:${m.email}`} className="text-sm underline">{m.email}</a>
            <span className="ml-auto text-xs opacity-60">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <p className="mt-2 whitespace-pre-wrap">{m.message}</p>
          <div className="mt-2 flex gap-2">
            {!m.read && <Button size="sm" variant="outline" className="rounded-full" onClick={() => read(m.id)} data-testid={`admin-message-read-${m.id}`}>Marquer comme lu</Button>}
            <Button size="sm" variant="ghost" onClick={() => remove(m.id)} aria-label="Supprimer" data-testid={`admin-message-delete-${m.id}`}><Trash2 className="h-4 w-4 text-[#F0303A]" /></Button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const NewsletterTab = ({ call }) => {
  const [subs, setSubs] = useState([]);
  const load = () => call("get", "/admin/subscribers").then(setSubs).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    try {
      const data = await call("get", `/admin/subscribers.csv?origin=${encodeURIComponent(window.location.origin)}`);
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "newsletter-mizubini.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* toast shown */ }
  };
  const remove = async (email) => {
    if (!window.confirm(`Supprimer définitivement ${email} ?`)) return;
    try { await call("delete", `/admin/subscribers/${encodeURIComponent(email)}`); } catch { return; } load();
  };
  const active = subs.filter((s) => s.status === "subscribed").length;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-semibold" data-testid="admin-subscribers-count">{active} inscrit(s) actif(s)</p>
        <Button onClick={exportCsv} variant="outline" className="rounded-full" data-testid="admin-subscribers-export"><Download className="mr-2 h-4 w-4" /> Exporter en CSV</Button>
      </div>
      <p className="mt-2 text-sm opacity-75">Le fichier contient, pour chaque inscrit, un lien de désinscription personnel à mettre dans vos envois.</p>
      <ul className="mt-4 space-y-2" data-testid="admin-subscribers-list">
        {subs.map((s) => (
          <li key={s.email} className="flex flex-wrap items-center gap-3 rounded-[16px] bg-white px-4 py-2 text-sm shadow-sm">
            <span className="flex-1 font-semibold break-all">{s.email}</span>
            <span className={s.status === "subscribed" ? "text-[#3a9a10]" : "opacity-60"}>{s.status === "subscribed" ? "Inscrit" : "Désinscrit"}</span>
            <span className="opacity-60">{s.consent_at && new Date(s.consent_at).toLocaleDateString("fr-FR")}</span>
            <Button size="sm" variant="ghost" aria-label="Supprimer" onClick={() => remove(s.email)} data-testid={`admin-subscriber-delete-${s.email}`}><Trash2 className="h-4 w-4 text-[#F0303A]" /></Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
